import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { 
  Users, 
  FileText, 
  Send, 
  Mail,
  MousePointerClick,
  Eye,
  AlertTriangle,
  TrendingUp
} from "lucide-react";

export const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await apiClient.get("/analytics/overview");
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-sky-600 rounded-full animate-spin" />
      </div>
    );
  }

  // Helper to format limit display - shows actual count vs max
  const formatLimit = (current, max) => {
    if (max === null || max === undefined) return `${current}`; // Unlimited - just show current
    return `${current}/${max}`;
  };
  
  // Helper to get max value, handling null as unlimited
  const getMaxValue = (primary, secondary, defaultVal) => {
    if (primary !== undefined) return primary;
    if (secondary !== undefined) return secondary;
    return defaultVal;
  };

  const statCards = [
    { 
      label: "Total Contacts", 
      value: formatLimit(stats?.contacts?.total || 0, stats?.plan?.max_contacts),
      icon: Users, 
      color: "bg-blue-500",
      description: stats?.plan?.max_contacts === null ? "Unlimited" : "People in your list",
      link: "/contacts"
    },
    { 
      label: "Templates", 
      value: formatLimit(stats?.templates || 0, getMaxValue(stats?.max_templates, stats?.plan?.max_templates, 1)),
      icon: FileText, 
      color: "bg-amber-500",
      description: stats?.plan?.max_templates === null ? "Unlimited" : "Email templates created",
      link: "/templates"
    },
    { 
      label: "Campaigns This Month", 
      value: formatLimit(stats?.campaigns?.sent_this_month || 0, stats?.plan?.max_campaigns_per_month),
      icon: Send, 
      color: "bg-emerald-500",
      description: stats?.plan?.max_campaigns_per_month === null ? "Unlimited" : "Resets monthly",
      link: "/campaigns"
    },
    { 
      label: "Emails Sent", 
      value: stats?.total_sent || stats?.emails?.sent || 0, 
      icon: Mail, 
      color: "bg-slate-600",
      description: "Total emails delivered",
      link: "/analytics"
    },
  ];

  const performanceCards = [
    { 
      label: "Open Rate", 
      value: `${stats?.open_rate || 0}%`, 
      icon: Eye, 
      trend: stats?.open_rate > 20 ? "Good" : "Needs Work",
      trendUp: stats?.open_rate > 20
    },
    { 
      label: "Click Rate", 
      value: `${stats?.click_rate || 0}%`, 
      icon: MousePointerClick, 
      trend: stats?.click_rate > 5 ? "Good" : "Needs Work",
      trendUp: stats?.click_rate > 5
    },
    { 
      label: "Bounce Rate", 
      value: `${stats?.bounce_rate || 0}%`, 
      icon: AlertTriangle, 
      trend: stats?.bounce_rate < 5 ? "Good" : "High",
      trendUp: stats?.bounce_rate < 5
    },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1">Overview of your email campaigns</p>
        </div>
        <div className="hidden md:flex items-center gap-2 bg-sky-50 text-sky-700 px-4 py-2 rounded-lg border border-sky-200">
          <TrendingUp className="w-4 h-4" />
          <span className="font-medium text-sm">eSay Pro</span>
        </div>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {statCards.map((stat) => (
          <Card 
            key={stat.label} 
            className="border border-slate-200 bg-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
            onClick={() => navigate(stat.link)}
          >
            <CardContent className="pt-5 pb-5">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0 pr-3">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{stat.label}</p>
                  <p className="text-3xl font-bold mt-2 text-slate-900">{stat.value}</p>
                  <p className="text-xs text-slate-400 mt-1">{stat.description}</p>
                </div>
                <div className={`w-11 h-11 ${stat.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <stat.icon className="w-5 h-5 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Performance Metrics */}
      <div>
        <h2 className="text-xl font-semibold text-slate-900 mb-4">Performance Metrics</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {performanceCards.map((stat) => (
            <Card 
              key={stat.label} 
              className="border border-slate-200 bg-white shadow-sm hover:shadow-md transition-all duration-200"
            >
              <CardContent className="pt-5 pb-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <stat.icon className="w-5 h-5 text-slate-600" />
                  </div>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                    stat.trendUp 
                      ? "bg-emerald-100 text-emerald-700" 
                      : "bg-amber-100 text-amber-700"
                  }`}>
                    {stat.trend}
                  </span>
                </div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{stat.label}</p>
                <p className="text-4xl font-bold mt-1 text-slate-900">{stat.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <Card className="border border-slate-200 bg-white shadow-sm">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50">
          <CardTitle className="text-lg font-semibold">Quick Start Guide</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div 
              className="p-5 bg-slate-50 rounded-lg border border-slate-100 hover:bg-sky-50 hover:border-sky-200 transition-colors group cursor-pointer"
              onClick={() => navigate("/contacts")}
            >
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4 group-hover:scale-105 transition-transform" style={{backgroundColor: '#165581'}}>
                <span className="text-white font-bold">1</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">Upload Contacts</h3>
              <p className="text-sm text-slate-500">Import your spreadsheet with business contacts</p>
            </div>
            <div 
              className="p-5 bg-slate-50 rounded-lg border border-slate-100 hover:bg-sky-50 hover:border-sky-200 transition-colors group cursor-pointer"
              onClick={() => navigate("/templates")}
            >
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4 group-hover:scale-105 transition-transform" style={{backgroundColor: '#165581'}}>
                <span className="text-white font-bold">2</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">Create Templates</h3>
              <p className="text-sm text-slate-500">Design personalized email templates</p>
            </div>
            <div 
              className="p-5 bg-slate-50 rounded-lg border border-slate-100 hover:bg-sky-50 hover:border-sky-200 transition-colors group cursor-pointer"
              onClick={() => navigate("/campaigns")}
            >
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4 group-hover:scale-105 transition-transform" style={{backgroundColor: '#165581'}}>
                <span className="text-white font-bold">3</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">Send Campaigns</h3>
              <p className="text-sm text-slate-500">Launch your email campaign</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
